import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { CollaborationInvite } from '../types/collaboration';
import { useAuth } from './useAuth';

interface UseCollaborationInvitesReturn {
  invites: CollaborationInvite[];
  loading: boolean;
  error: Error | null;
}

/**
 * Custom hook to fetch and subscribe to collaboration invites for current user
 * Uses real-time Firestore listener for live updates
 */
export const useCollaborationInvites = (): UseCollaborationInvitesReturn => {
  const { user } = useAuth();
  const [invites, setInvites] = useState<CollaborationInvite[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset state if no user
    if (!user || !user.email) {
      setInvites([]);
      setLoading(false);
      return;
    }

    try {
      // Query pending invites for the current user's email
      const q = query(
        collection(db, 'collaborationInvites'),
        where('toEmail', '==', user.email),
        where('status', '==', 'pending')
      );

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const invitesList: CollaborationInvite[] = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              inviteId: doc.id,
              projectId: data.projectId,
              fromUserId: data.fromUserId,
              fromUserEmail: data.fromUserEmail,
              toEmail: data.toEmail,
              status: data.status,
              createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : new Date(data.createdAt),
              respondedAt: data.respondedAt instanceof Timestamp
                ? data.respondedAt.toDate()
                : data.respondedAt ? new Date(data.respondedAt) : undefined,
            };
          });

          setInvites(invitesList);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching collaboration invites:', err);
          setError(err instanceof Error ? err : new Error('Failed to fetch collaboration invites'));
          setLoading(false);
        }
      );

      // Cleanup listener on unmount
      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up collaboration invites listener:', err);
      setError(err instanceof Error ? err : new Error('Failed to set up collaboration invites listener'));
      setLoading(false);
    }
  }, [user]);

  return { invites, loading, error };
};
